import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const STORAGE_KEY = 'eaglevizion_cookie_consent';

const C = {
  blue:       '#004395',
  blueDark:   '#003070',
  blueLight:  'rgba(0,67,149,0.15)',
  yellow:     '#75aff5',
  dark:       '#1d2433',
  darkPanel:  '#11202D',
  mutedText:  '#ffffff',
  white:      '#ffffff',
  offWhite:   '#f0f3f7',
  border:     'rgba(255,255,255,0.10)',
  font:       "'Roboto', sans-serif",
};

const EN = {
  badge:     'Quebec Law 25 Compliant',
  title:     'Privacy & Cookies',
  body:      'We use cookies to enhance your browsing experience, deliver personalised content and analyse traffic. By clicking "Accept All", you consent to our use of cookies.',
  learnMore: 'Learn more',
  accept:    'Accept All',
  decline:   'Decline',
};

const FR = {
  badge:     'Conforme à la Loi 25 du Québec',
  title:     'Confidentialité & Cookies',
  body:      'Nous utilisons des cookies pour améliorer votre expérience, diffuser des contenus personnalisés et analyser le trafic. En cliquant sur « Tout accepter », vous consentez à notre utilisation des cookies.',
  learnMore: 'En savoir plus',
  accept:    'Tout accepter',
  decline:   'Refuser',
};

const CookieBanner = () => {
  const [visible, setVisible]     = useState(!localStorage.getItem(STORAGE_KEY));
  const [animating, setAnimating] = useState(false);
  const { currentLanguage } = useLanguage();

  const t = currentLanguage === 'fr' ? FR : EN;

  useEffect(() => {
    if (!visible) return;
    const id = setTimeout(() => requestAnimationFrame(() => setAnimating(true)), 900);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = (choice) => {
    localStorage.setItem(STORAGE_KEY, choice);
    setAnimating(false);
    setTimeout(() => setVisible(false), 460);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(17,32,45,0.55)',
          zIndex: 9998,
          opacity: animating ? 1 : 0,
          transition: 'opacity 0.46s ease',
          pointerEvents: 'none',
        }}
      />

      {/* Banner */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cookie consent"
        style={{
          position: 'fixed',
          bottom: animating ? '0px' : '-100%',
          left: 0, right: 0,
          zIndex: 9999,
          background: C.darkPanel,
          borderTop: `3px solid transparent`,
          borderImage: `linear-gradient(90deg, #222 0%, ${C.blue} 40%, #6b7b8d 70%, #222 100%) 1`,
          boxShadow: '0 -8px 40px rgba(0,0,0,0.45)',
          transition: 'bottom 0.52s cubic-bezier(0.34, 1.10, 0.64, 1)',
          fontFamily: C.font,
        }}
      >
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '20px 36px',
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          flexWrap: 'wrap',
        }}>

          {/* Icon box */}
          <div style={{
            flexShrink: 0,
            width: '48px', height: '48px',
            background: C.blueLight,
            border: `1px solid ${C.blue}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
              stroke={C.yellow} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="2.5" />
              <line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="2.5" />
            </svg>
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span style={{
                background: C.blue,
                color: C.white,
                fontSize: '9px',
                fontWeight: '700',
                letterSpacing: '1.6px',
                textTransform: 'uppercase',
                padding: '3px 10px',
              }}>
                {t.badge}
              </span>
              <h3 style={{
                color: C.white,
                fontSize: '15px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '0.3px',
              }}>
                {t.title}
              </h3>
            </div>
            <p style={{
              color: C.mutedText,
              fontSize: '13px',
              lineHeight: '1.6',
              margin: 0,
            }}>
              {t.body}{' '}
              <a
                href={`${process.env.PUBLIC_URL}/Politique_de_confidentialité`}
                style={{
                  color: C.yellow,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  fontSize: '13px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                {t.learnMore}
              </a>
            </p>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            flexShrink: 0,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            {/* Decline */}
            <button
              onClick={() => dismiss('declined')}
              style={{
                background: 'transparent',
                color: C.mutedText,
                border: `1.5px solid rgba(255,255,255,0.20)`,
                padding: '10px 24px',
                fontSize: '12px',
                fontWeight: '600',
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                fontFamily: C.font,
                cursor: 'pointer',
                transition: 'border-color 0.2s, color 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.50)';
                e.currentTarget.style.color = C.offWhite;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.20)';
                e.currentTarget.style.color = C.mutedText;
              }}
            >
              {t.decline}
            </button>

            {/* Accept */}
            <button
              onClick={() => dismiss('accepted')}
              style={{
                background: C.blue,
                color: C.white,
                border: `1.5px solid ${C.blue}`,
                padding: '10px 28px',
                fontSize: '12px',
                fontWeight: '700',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                fontFamily: C.font,
                cursor: 'pointer',
                transition: 'background 0.2s, border-color 0.2s, transform 0.18s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.blueDark;
                e.currentTarget.style.borderColor = C.blueDark;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = C.blue;
                e.currentTarget.style.borderColor = C.blue;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              ✓&nbsp; {t.accept}
            </button>
          </div>
        </div>
        <div style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
      </div>
    </>
  );
};

export default CookieBanner;
